import { pdfBase64 } from "pkg-pdf-generator";
import {
  preprocessPdfLayoutTextValues,
  resolveSignatureRefs
} from "src/utils/helper";

// pdfmake's _createDoc wraps createPdfKitDocument in a urlResolver.then(...)
// with no .catch — when createPdfKitDocument throws on a bad layout (e.g.
// invalid column widths, unknown font), the rejection escapes as global
// unhandledrejection and the success callback never fires. The wrapping
// promise from pkg-pdf-generator's genFrontPdf has no reject path, so
// pdfBase64 just hangs and the builder spins forever. We surface those
// failures here: listen for the global rejection during the render call
// (so we can show the real error) and keep a timeout backstop in case
// nothing rejects either.
const PDF_RENDER_TIMEOUT_MS = 10000;

export async function renderPreview({ layout, sampleData, addOn = null }) {
  try {
    if (!layout) throw new Error("renderPreview: layout is required");
    const data = sampleData ? deepCopy(sampleData) : {};
    // preprocessPdfLayoutTextValues mutates the layout in place — clone
    // first or the mutation re-triggers the builder's deep watch and we
    // infinite-loop the preview.
    const layoutCopy = deepCopy(layout);

    // Mirrors pdfLayoutsStore.generatePDFBase64 — non-partial add-on
    // renders separately and is appended via appendPDFData.
    let mergedData = data;
    if (addOn && !addOn.isPartial && addOn.content) {
      const addOnRender = await renderPreview({
        layout: addOn.content,
        sampleData: data
      });
      if (addOnRender.ok) {
        mergedData = {
          ...mergedData,
          appendPDFData: {
            value: addOnRender.base64,
            name: "addOnPDF",
            type: "application/pdf"
          }
        };
      }
    }

    const processedLayout = preprocessPdfLayoutTextValues(
      layoutCopy,
      mergedData
    );
    const resolvedFormData = await resolveSignatureRefs(mergedData);
    const base64 = await pdfBase64Safe(processedLayout, resolvedFormData);
    return { ok: true, base64 };
  } catch (err) {
    console.warn("[pdfBuilder] preview render failed", err);
    return { ok: false, error: err };
  }
}

function pdfBase64Safe(layout, data) {
  return new Promise((resolve, reject) => {
    let settled = false;
    let timeoutHandle = null;

    const cleanup = () => {
      if (timeoutHandle != null) clearTimeout(timeoutHandle);
      if (typeof window !== "undefined") {
        window.removeEventListener("unhandledrejection", onUnhandled);
      }
    };

    const settleOnce = (fn, value) => {
      if (settled) return;
      settled = true;
      cleanup();
      fn(value);
    };

    const onUnhandled = (event) => {
      // pdfmake's broken rejection chain lands here. Treat the reason as
      // the render error and prevent the default console noise.
      event.preventDefault();
      const reason = event.reason;
      const err =
        reason instanceof Error
          ? reason
          : new Error(
              typeof reason === "string" && reason
                ? reason
                : "PDF render failed (invalid layout)."
            );
      settleOnce(reject, err);
    };

    if (typeof window !== "undefined") {
      window.addEventListener("unhandledrejection", onUnhandled);
    }

    timeoutHandle = setTimeout(() => {
      settleOnce(
        reject,
        new Error(
          "PDF render timed out — the layout may contain invalid values."
        )
      );
    }, PDF_RENDER_TIMEOUT_MS);

    pdfBase64(layout, data).then(
      (b64) => settleOnce(resolve, b64),
      (err) =>
        settleOnce(reject, err instanceof Error ? err : new Error(String(err)))
    );
  });
}

function deepCopy(o) {
  try {
    return JSON.parse(JSON.stringify(o));
  } catch {
    return {};
  }
}
